(function () {
  'use strict';

  // Guards against this script running more than once on the same page.
  // snippets/rsi-warehouse-optimiser-gate.liquid renders its own
  // "<script src>" tag on every render, and this page always has at least
  // two: the real, page-specific gate PLUS the generic fallback instance
  // snippets/rsi-header-mobile-nav.liquid renders on every page for the
  // drawer's own trigger link. Without this guard the whole IIFE below
  // runs twice, attaching two separate 'submit' listeners to the same
  // form. Same pattern already used by assets/rsi-mobile-header.js for
  // the same class of problem.
  if (window.__rsiWoGateInit) return;
  window.__rsiWoGateInit = true;

  /* ============================================================
     RSI WAREHOUSE OPTIMISER — Lead-capture gate
     No persisted unlock state on THIS page: every launch attempt must
     submit the form again.

     1. Launch-link interception (modal) + hard-lock mode (auto-shown,
        used only by the legacy embedded-iframe planner page).
     2. Validation
     3. Submission (webhook JSON, or Shopify /contact fallback)
     4. SUCCESS DETECTION: "{% form 'contact' %}" (snippets/rsi-warehouse-
        optimiser-gate.liquid) renders "form.posted_successfully?"/
        "form.errors" into a "[data-rsi-wo-gate-posted]" marker — a real,
        server-evaluated signal, not a guessed URL parameter.
     5. On success, the liquid renders a genuine "<a>" link straight to the
        optimiser (see the success panel markup) — plain HTML, no JS
        required to navigate it. This file does not build, track, or
        navigate that link in any way; it only reveals the panel it's in.

     A previous version of this file added a cross-origin nonce/postMessage
     handoff so the success link would auto-open a pre-opened tab and the
     optimiser would skip its own gate. That added several new failure
     points (missing sessionStorage value, popup blocked, listener never
     attached, reload racing the handshake) for one convenience win, and it
     broke in the field. Removed entirely — the plain link below always
     works, including with JavaScript disabled.
  ============================================================ */

  var LOG_PREFIX = '[RSI WO GATE]';
  var FIELDS_KEY = 'rsi_wo_gate_pending_fields';
  // Marks "this exact gate form is mid-submission" right before the real
  // native /contact POST — see the redirect-detection block below for why.
  var PENDING_KEY = 'rsi_wo_gate_pending_submission';
  var PENDING_MAX_AGE_MS = 5 * 60 * 1000;

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '');
  }

  // Strips everything but digits/+, folds +44 / 0044 down to a leading 0
  // so a single UK-format check covers however the user typed it in.
  function normalisePhone(value) {
    var digits = (value || '').replace(/[^\d+]/g, '');
    if (digits.indexOf('+44') === 0) {
      digits = '0' + digits.slice(3);
    } else if (digits.indexOf('0044') === 0) {
      digits = '0' + digits.slice(4);
    }
    return digits;
  }

  function isValidPhone(value) {
    return /^0\d{9,10}$/.test(normalisePhone(value));
  }

  function toE164(value) {
    var normalised = normalisePhone(value);
    if (/^0\d{9,10}$/.test(normalised)) {
      return '+44' + normalised.slice(1);
    }
    return value;
  }

  var gates = Array.prototype.slice.call(document.querySelectorAll('[data-rsi-wo-gate]'));
  if (!gates.length) return;

  // Only one gate is ever wired up per page. Normally there's exactly one,
  // but snippets/rsi-header-mobile-nav.liquid renders a fallback instance
  // (marked [data-rsi-wo-gate-generic]) on every page so drawer links
  // always have a gate to open — on a page that already renders its own
  // (e.g. the lock-mode embedded planner page), that real instance must
  // win, or a single trigger click would open two competing modals.
  var activeGate = gates.length === 1
    ? gates[0]
    : (gates.filter(function (g) { return g.hasAttribute('data-rsi-wo-gate-lock-mode'); })[0]
      || gates.filter(function (g) { return !g.hasAttribute('data-rsi-wo-gate-generic'); })[0]
      || gates[0]);

  // Shopify's own "form.posted_successfully?" is not scoped per form id —
  // confirmed live: after one real successful submission, EVERY
  // "{% form 'contact' %}" instance on the page reports success, not just
  // the one actually submitted. Since the generic drawer-fallback gate
  // (snippets/rsi-header-mobile-nav.liquid, rendered on every page) has
  // its own independent copy of the same form, this means BOTH it and the
  // real page gate render their own success panel on the exact same
  // reload, and assets/rsi-warehouse-optimiser-gate.css's own ":has()"
  // fallback (for the "JS never loaded" case) force-shows BOTH of them —
  // stacked, full-screen, overlapping. Only "activeGate" above ever gets
  // "setupGate()" called on it, so the OTHER one's close button had no
  // listener at all and its overlay sat on top intercepting clicks meant
  // for the real one — the exact "X does nothing, have to hit browser
  // back" report. Fixed by making close global: every gate's close
  // button/scrim (wired below, on ALL instances, not just activeGate)
  // dismisses every gate on the page in one action.
  function closeAllGates() {
    gates.forEach(function (g) {
      if (g.hasAttribute('data-rsi-wo-gate-lock-mode')) return; // no dismiss in hard-lock mode
      g.setAttribute('hidden', 'hidden');
      g.setAttribute('data-rsi-wo-gate-dismissed', 'true');
    });
    try {
      document.body.classList.remove('rsi-wo-gate-open', 'overflow-hidden', 'fixed');
      document.body.style.removeProperty('top');
    } catch (e) {
      console.warn(LOG_PREFIX, 'body-lock cleanup threw, continuing anyway:', e);
    }
  }

  gates.forEach(function (g) {
    var gCloseBtn = g.querySelector('[data-rsi-wo-gate-close]');
    var gScrim = g.querySelector('[data-rsi-wo-gate-scrim]');
    var gIsLockMode = g.hasAttribute('data-rsi-wo-gate-lock-mode');
    if (gCloseBtn) gCloseBtn.addEventListener('click', closeAllGates);
    if (gScrim && !gIsLockMode) gScrim.addEventListener('click', closeAllGates);
  });

  setupGate(activeGate);

  function setupGate(gateRoot) {
    var isLockMode = gateRoot.hasAttribute('data-rsi-wo-gate-lock-mode');
    var webhookUrl = (gateRoot.getAttribute('data-rsi-wo-gate-webhook') || '').trim();
    // Shopify's own "{% form 'contact' %}" tag generates the real <form>
    // element — no hand-placed marker attribute needed to find it, and
    // none is possible: the tag doesn't pass arbitrary data-* attributes
    // through to the element it emits.
    var form = gateRoot.querySelector('form');
    var submitBtn = gateRoot.querySelector('[data-rsi-wo-gate-submit]');
    var errorSummary = gateRoot.querySelector('[data-rsi-wo-gate-error-summary]');
    var submitOrigText = submitBtn ? submitBtn.textContent : '';

    function stripBodyLocks() {
      // Wrapped so a throw here (a real checklist concern) can never
      // block anything that follows it.
      try {
        document.body.classList.remove('rsi-wo-gate-open', 'overflow-hidden', 'fixed');
        document.body.style.removeProperty('top');
      } catch (e) {
        console.warn(LOG_PREFIX, 'body-lock cleanup threw, continuing anyway:', e);
      }
    }

    function lockStage() {
      document.querySelectorAll('[data-rsi-wo-gate-stage]').forEach(function (el) {
        el.classList.add('is-rsi-wo-locked');
        // "is-rsi-wo-locked" only ever applied filter:blur + pointer-events:
        // none — neither stops keyboard Tab navigation, so a user tabbing
        // through the page could still reach and use the embedded
        // iframe's own fully-unlocked controls without ever touching the
        // gate at all. "inert" (native, no JS focus-trap of our own to
        // maintain) removes the whole stage from the tab order and from
        // being targeted at all while locked; the "tabindex=-1" alongside
        // it is a plain fallback for the rare browser without "inert"
        // support, where it at least keeps the iframe itself out of tab
        // order even though "inert" itself no-ops there.
        el.setAttribute('inert', '');
        el.querySelectorAll('iframe').forEach(function (frame) {
          if (!frame.hasAttribute('data-rsi-wo-gate-prior-tabindex')) {
            frame.setAttribute('data-rsi-wo-gate-prior-tabindex', frame.getAttribute('tabindex') || '');
          }
          frame.setAttribute('tabindex', '-1');
        });
      });
    }

    function unlockStage() {
      document.querySelectorAll('[data-rsi-wo-gate-stage]').forEach(function (el) {
        el.classList.remove('is-rsi-wo-locked');
        el.removeAttribute('inert');
        el.querySelectorAll('iframe').forEach(function (frame) {
          var prior = frame.getAttribute('data-rsi-wo-gate-prior-tabindex');
          frame.removeAttribute('data-rsi-wo-gate-prior-tabindex');
          if (prior) {
            frame.setAttribute('tabindex', prior);
          } else {
            frame.removeAttribute('tabindex');
          }
        });
      });
    }

    function openModal() {
      gateRoot.removeAttribute('hidden');
      // Only meaningful while "hidden" is present (see the CSS rule this
      // pairs with) — cleared here too so a later close/reopen cycle
      // doesn't leave a stale marker behind.
      gateRoot.removeAttribute('data-rsi-wo-gate-dismissed');
      document.body.classList.add('rsi-wo-gate-open');
      window.setTimeout(function () {
        var firstField = form && form.querySelector('input:not([type="hidden"]), select');
        if (firstField) firstField.focus();
      }, 10);
    }

    function closeModal() {
      if (isLockMode) return; // nothing useful behind it — no dismiss in hard-lock mode
      // Delegates to the page-level closeAllGates() (see above) rather
      // than only hiding this instance — Shopify's own success flag isn't
      // scoped per form id, so the generic drawer-fallback gate can end
      // up showing its own success panel at the same time as this one;
      // closing must dismiss whichever gates are actually visible, not
      // just the one this particular call happened to originate from.
      closeAllGates();
    }

    function finishUnlock() {
      if (isLockMode) {
        unlockStage();
        gateRoot.setAttribute('hidden', 'hidden');
        stripBodyLocks();
        return;
      }
      // Non-lock-mode, native /contact success: nothing to do here. The
      // success panel (rendered by the liquid once form.posted_
      // successfully? is true) already contains a plain "<a href>"
      // straight to the optimiser — that's the entire "unlock" action,
      // and it needs no JS to work.
    }

    // Webhook path only (currently unconfigured in production — blank
    // "data-rsi-wo-gate-webhook"). Unlike the native /contact path, a
    // webhook success has no page reload, so the liquid never gets a
    // chance to render the success panel's real anchor. As a single,
    // plain, best-effort action (not the removed nonce/tab-tracking
    // system) it just opens the same configured destination directly —
    // if this is blocked, the user is left on a closed modal with
    // nothing broken, since no gate/access state depends on it.
    function openOptimiserAfterWebhookSuccess() {
      var destination = gateRoot.getAttribute('data-rsi-wo-gate-optimiser-url');
      if (destination) {
        try { window.open(destination, '_blank', 'noopener'); } catch (e) {}
      }
      closeModal();
    }

    function showServerError(message) {
      if (!errorSummary) return;
      errorSummary.hidden = false;
      errorSummary.textContent = message || 'Something went wrong sending your details. Please try again or call 01827 213073.';
    }

    function stashFieldValues() {
      // Shopify's native form.name/form.email/form.phone repopulate
      // themselves automatically after a reload (see the liquid), but the
      // custom fields (company/postcode/planning reason/consent) aren't
      // exposed by the form drop at all — this backs those specific ones
      // up so a real server-side rejection doesn't silently blank them.
      if (!form) return;
      try {
        var data = {
          company: fieldValue('[name="contact[Company name]"]'),
          postcode: fieldValue('[name="contact[Site postcode]"]'),
          reason: fieldValue('[name="contact[What are you planning?]"]'),
          consent: !!(form.querySelector('[data-rsi-wo-gate-consent]') || {}).checked
        };
        window.sessionStorage.setItem(FIELDS_KEY, JSON.stringify(data));
      } catch (e) {}
    }

    function restoreFieldValues() {
      if (!form) return;
      var raw = null;
      try { raw = window.sessionStorage.getItem(FIELDS_KEY); } catch (e) {}
      if (!raw) return;
      try { window.sessionStorage.removeItem(FIELDS_KEY); } catch (e) {}
      var data;
      try { data = JSON.parse(raw); } catch (e) { return; }
      var companyField = form.querySelector('[name="contact[Company name]"]');
      var postcodeField = form.querySelector('[name="contact[Site postcode]"]');
      var reasonField = form.querySelector('[name="contact[What are you planning?]"]');
      var consentField = form.querySelector('[data-rsi-wo-gate-consent]');
      if (companyField && data.company) companyField.value = data.company;
      if (postcodeField && data.postcode) postcodeField.value = data.postcode;
      if (reasonField && data.reason) reasonField.value = data.reason;
      if (consentField && data.consent) consentField.checked = true;
    }

    // ---- Redirect-detection: resume after a native Shopify /contact submit ----
    // "postedMarker" is the always-present div snippets/rsi-warehouse-
    // optimiser-gate.liquid renders inside the {% form %} block:
    //   ""      — no submission happened on this exact request (fresh load)
    //   "true"  — Shopify just confirmed A contact-type form posted successfully
    //   "false" — Shopify just rejected/errored A contact-type form submission
    //
    // BUG (confirmed live): Shopify's "form.posted_successfully?" is NOT
    // scoped per form id — it reflects "was ANY contact-type form on this
    // site just posted successfully", not "was THIS specific form
    // submitted". Pages that carry their own real gate instance alongside
    // an unrelated contact form (sections/rsi-get-a-quote.liquid renders
    // both this gate AND its own separate "Request a quote" form) — plus
    // every page via the generic mobile-nav fallback gate — would
    // therefore show the Warehouse Optimiser "You're unlocked" success
    // panel after submitting a COMPLETELY UNRELATED form (Get A Quote,
    // Racking Installation, Contact, etc), since that unrelated form's own
    // successful post-back makes THIS gate's "postedValue" false-positive
    // to "true" as well on the very same reload.
    //
    // Fix: only ever trust postedValue when a matching PENDING_KEY entry
    // proves THIS exact gate form (by its own unique DOM id) was the one
    // actually submitted, stashed in sessionStorage immediately before the
    // real form.submit() call below and read back (and always cleared)
    // here on load. An unrelated form's native submit never touches this
    // key, so it can never fool a gate it didn't originate from — this
    // preserves the legitimate "unlock straight from the Get A Quote page
    // / mobile-nav drawer, on any page" flows the generic + per-page gate
    // instances are deliberately designed to support, unlike a hard
    // page-path lock (which would have broken those working flows too).
    var unlockedThisView = false;
    var postedMarker = form ? form.querySelector('[data-rsi-wo-gate-posted]') : null;
    var postedValue = postedMarker ? postedMarker.getAttribute('data-rsi-wo-gate-posted') : '';

    var isOurPendingSubmission = false;
    try {
      var pendingRaw = window.sessionStorage.getItem(PENDING_KEY);
      if (pendingRaw) {
        window.sessionStorage.removeItem(PENDING_KEY);
        var pending = JSON.parse(pendingRaw);
        var pendingFresh = pending && typeof pending.ts === 'number' && (Date.now() - pending.ts) < PENDING_MAX_AGE_MS;
        isOurPendingSubmission = !!(pendingFresh && form && pending.formId && pending.formId === form.id);
      }
    } catch (e) {}

    if (postedValue === 'true' && isOurPendingSubmission) {
      try { window.sessionStorage.removeItem(FIELDS_KEY); } catch (e) {}
      unlockedThisView = true;
      if (!isLockMode) {
        // The gate root defaults to "hidden" on every fresh server
        // render (including this exact reload) — openModal() is what
        // actually reveals the success panel the liquid just rendered.
        openModal();
      }
      finishUnlock();
    } else if (postedValue === 'false' && isOurPendingSubmission) {
      // A real submission just failed server-side (not a client-side
      // validation failure — those never leave this page at all).
      console.warn(LOG_PREFIX, 'Shopify rejected the submission — reopening gate with error.');
      restoreFieldValues();
      if (!isLockMode) openModal();
      showServerError();
    } else if (postedValue === 'true' || postedValue === 'false') {
      // Shopify reports a real post-back happened, but not one that came
      // from THIS gate's own form — an unrelated form's success/failure on
      // the same page. Deliberately inert: no modal, no JS state change —
      // but the Liquid success panel markup still exists in the DOM (Liquid
      // has no way to avoid rendering it), and assets/rsi-warehouse-
      // optimiser-gate.css has a ":has()" no-JS-fallback rule that force-
      // shows the gate purely from that markup's presence, independent of
      // this script. Setting the SAME "dismissed" marker closeAllGates()
      // already uses to suppress that CSS rule stops the false positive
      // from visually appearing even a moment before this line runs.
      console.warn(LOG_PREFIX, 'Ignoring posted_successfully? from an unrelated form on this page.');
      gateRoot.setAttribute('data-rsi-wo-gate-dismissed', 'true');
    }

    // ---- Hard-lock mode: show immediately every load, unless the block
    // above just resolved this exact view via a same-attempt redirect ----
    if (isLockMode) {
      if (!unlockedThisView) {
        lockStage();
        openModal();
      }
    }

    // ---- Launch-link interception: always gate, every click ----
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-rsi-wo-gate-trigger]');
      if (!trigger) return;

      event.preventDefault();
      openModal();
    });

    // closeBtn/scrim for this instance are already wired above (every
    // gate on the page gets one, not just this "active" one) — nothing
    // further needed here.

    if (!form) return;

    // Populate hidden context fields once on load.
    var params = new URLSearchParams(window.location.search);
    setHiddenField('page_url', window.location.href);
    setHiddenField('utm_source', params.get('utm_source') || '');
    setHiddenField('utm_medium', params.get('utm_medium') || '');
    setHiddenField('utm_campaign', params.get('utm_campaign') || '');

    function setHiddenField(key, value) {
      var el = form.querySelector('[data-rsi-wo-gate-hidden="' + key + '"]');
      if (el) el.value = value;
    }

    function fieldError(name, message) {
      var el = form.querySelector('[data-rsi-wo-gate-error="' + name + '"]');
      if (el) el.textContent = message || '';
    }

    function clearErrors() {
      form.querySelectorAll('[data-rsi-wo-gate-error]').forEach(function (el) {
        el.textContent = '';
      });
      if (errorSummary) {
        errorSummary.hidden = true;
        errorSummary.textContent = '';
      }
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearErrors();

      var nameField = form.querySelector('[name="contact[name]"]');
      var emailField = form.querySelector('[name="contact[email]"]');
      var phoneField = form.querySelector('[name="contact[phone]"]');
      var consentField = form.querySelector('[data-rsi-wo-gate-consent]');

      var nameVal = nameField ? nameField.value.trim() : '';
      var emailVal = emailField ? emailField.value.trim() : '';
      var phoneVal = phoneField ? phoneField.value.trim() : '';

      var firstMessage = '';
      var firstInvalidEl = null;
      var hasError = false;

      function fail(field, message, el) {
        fieldError(field, message);
        if (!firstMessage) {
          firstMessage = message;
          firstInvalidEl = el;
        }
        hasError = true;
      }

      if (!nameVal) fail('name', 'Please enter your name.', nameField);
      if (!isValidEmail(emailVal)) fail('email', 'Please enter a valid email address.', emailField);
      if (!isValidPhone(phoneVal)) fail('phone', 'Please enter a valid phone number.', phoneField);
      if (!consentField || !consentField.checked) {
        fail('consent', 'Please confirm we can contact you about your layout.', consentField);
      }

      if (hasError) {
        if (errorSummary) {
          errorSummary.hidden = false;
          errorSummary.textContent = firstMessage;
        }
        if (firstInvalidEl) firstInvalidEl.focus();
        return;
      }

      var normalisedPhone = toE164(phoneVal);
      if (phoneField) phoneField.value = normalisedPhone;

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Unlocking…';
      }

      if (webhookUrl) {
        var payload = {
          source: 'Warehouse Optimiser Gate',
          page_url: window.location.href,
          name: nameVal,
          email: emailVal,
          phone: normalisedPhone,
          company: fieldValue('[name="contact[Company name]"]'),
          postcode: fieldValue('[name="contact[Site postcode]"]'),
          planning_reason: fieldValue('[name="contact[What are you planning?]"]'),
          consent: true,
          timestamp: new Date().toISOString(),
          utm_source: params.get('utm_source') || '',
          utm_medium: params.get('utm_medium') || '',
          utm_campaign: params.get('utm_campaign') || '',
          referrer: document.referrer || '',
          optimiser_access_granted: true
        };

        fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json().catch(function () { return null; });
        }).then(function (body) {
          // Treat 200-299 as success only when the body actually confirms
          // it (falls back to "any 2xx" if the endpoint returns no body/a
          // non-JSON body, since not every webhook echoes a payload back).
          var confirmed = body == null || body.success === true || body.success === undefined;
          if (!confirmed) throw new Error('Webhook responded 2xx but did not confirm success');
          if (isLockMode) {
            finishUnlock();
          } else {
            openOptimiserAfterWebhookSuccess();
          }
        }).catch(function (err) {
          console.warn(LOG_PREFIX, 'submission error:', err);
          showServerError();
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = submitOrigText;
          }
        });
        return;
      }

      // No webhook configured — native Shopify /contact submission via the
      // real "{% form 'contact' %}" tag now rendering this element. This is
      // a real page navigation; success/failure is resolved entirely by
      // "[data-rsi-wo-gate-posted]" above once it happens.
      stashFieldValues();
      try {
        window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ formId: form.id, ts: Date.now() }));
      } catch (e) {}
      form.submit();
    });

    function fieldValue(selector) {
      var el = form.querySelector(selector);
      return el ? el.value.trim() : '';
    }
  }
})();
